import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'src/utils/axios';
import SplashScreen from 'src/components/SplashScreen';
import { setUserData, logout } from 'src/actions/accountActions';
import { getEventTemplateTypes } from 'src/actions/eventTemplateTypesActions';
import { getTemplateCategories } from 'src/actions/templateCategoriesActions';
import authService from 'src/services/authService';

// Login-race 401 gate for the two post-login reference fetches.
//
// The bearer token is attached via axios.defaults.headers.common.Authorization
// (written by authService.setSession). On a fresh login into an immediate
// mount — and when React StrictMode double-invokes the effect — there is a
// window where the registry fetch (which fires first) goes out header-less
// and gets a fast 401, hanging the Host Templates tab and tripping the
// response interceptor's logout. This gate confirms the header is attached
// before dispatching. If a token exists but the header is somehow not set,
// it calls the existing public handleAuthentication() once to (re)attach it
// from the stored token, then dispatches. Registry-then-categories order is
// preserved. If the header still cannot be attached, the fetches are skipped
// rather than fired header-less into a 401.
export const dispatchReferenceFetchesWhenReady = (dispatch, thunks) => {
  const headerSet = () => !!axios?.defaults?.headers?.common?.Authorization;

  if (!headerSet()) {
    // Re-attach from the stored token using the existing public method.
    authService.handleAuthentication();
  }

  if (!headerSet()) {
    // Still no header — skip rather than fire a header-less 401.
    return;
  }

  dispatch(thunks.registry()).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('Failed to load event template types registry on login:', err);
  });

  dispatch(thunks.categories()).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('Failed to load template categories on login:', err);
  });
};

function Auth({ children }) {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      authService.setAxiosInterceptors({
        onLogout: () => dispatch(logout())
      });

      authService.handleAuthentication();

      if (authService.isAuthenticated()) {
        const payload = await authService.loginInWithToken();

        await dispatch(setUserData(payload));

        // Non-blocking fetch of the eventTemplateTypes registry (CON-1518)
        // and the Host Templates v2 template categories that drive
        // category-grouped rendering. Login UX must not be degraded by a
        // slow reference endpoint. Errors are swallowed by the action
        // creators and logged; each slice's loaded flag resolves so its
        // consumer stops showing a spinner. Routed through the gate so the
        // Authorization header is confirmed attached first — otherwise the
        // registry fetch can race ahead of the token and 401. Order stays
        // registry-then-categories.
        dispatchReferenceFetchesWhenReady(dispatch, {
          registry: getEventTemplateTypes,
          categories: getTemplateCategories,
        });
      }

      setLoading(false);
    };

    initAuth();
  }, [dispatch]);

  if (isLoading) {
    return <SplashScreen />;
  }

  return children;
}

Auth.propTypes = {
  children: PropTypes.any
};

export default Auth;
